import { periodDates } from '../../lib/payPeriods'
import { supabase } from '../../lib/supabase'
import { UNKNOWN_OFFICER } from './coverageReport'
import { resolveEntries, type GridEntry, type GridPerson, type GridPlacement, type PublishedEntry } from './payPeriodGridFigures'

type VersionRow = {
  team_id: string
  version: number
  entries: { employeeId?: string; employeeName?: string; date: string; category: string; label: string }[] | null
}

type PlacementRow = {
  employee_id: string
  team_id: string
  title: string
  effective_from: string
  effective_to: string | null
  shift_templates: { name: string } | null
  employees: { display_name: string; employee_identifier: string } | null
}

export type PayPeriodGridData = {
  people: GridPerson[]
  entries: GridEntry[]
  // The teams whose schedule for the period has been published.
  publishedTeamIds: Set<string>
}

/**
 * The latest published schedule of each team the signed-in account can read
 * for the pay period, and the officers placed on those teams during it.
 * Row-level security limits a supervisor to their own teams.
 */
export async function getPayPeriodGrid(periodNumber: number): Promise<PayPeriodGridData> {
  const dates = periodDates(periodNumber).map((day) => day.date)
  const first = dates[0]
  const last = dates[dates.length - 1]
  const [versionResult, placementResult] = await Promise.all([
    supabase.from('schedule_versions').select('team_id, version, entries').eq('period_start', first),
    supabase
      .from('employee_assignments')
      .select('employee_id, team_id, title, effective_from, effective_to, shift_templates(name), employees(display_name, employee_identifier)')
      .eq('active', true)
      .lte('effective_from', last)
      .or(`effective_to.is.null,effective_to.gte.${first}`),
  ])
  if (versionResult.error || placementResult.error) throw new Error('The pay period schedule could not be loaded.')

  const latest = new Map<string, VersionRow>()
  for (const row of (versionResult.data ?? []) as unknown as VersionRow[]) {
    const current = latest.get(row.team_id)
    if (!current || row.version > current.version) latest.set(row.team_id, row)
  }
  const publishedTeamIds = new Set(latest.keys())

  const published: PublishedEntry[] = []
  // The name a team published for each officer: the employee record is
  // readable only while the officer is on a team the caller supervises, so a
  // transferred officer is named from the snapshot.
  const publishedNames = new Map<string, string>()
  for (const version of latest.values()) {
    for (const entry of version.entries ?? []) {
      if (!entry.employeeId) continue
      published.push({ employeeId: entry.employeeId, teamId: version.team_id, date: entry.date, category: entry.category, label: entry.label })
      if (entry.employeeName) publishedNames.set(entry.employeeId, entry.employeeName)
    }
  }

  const placementRows = (placementResult.data ?? []) as unknown as PlacementRow[]
  const placements: GridPlacement[] = placementRows.map((row) => ({
    employeeId: row.employee_id,
    teamId: row.team_id,
    effectiveFrom: row.effective_from,
    effectiveTo: row.effective_to,
  }))
  const entries = resolveEntries(published, placements)

  // Someone placed twice in the period is listed once, on the later placement.
  const placed = new Map<string, PlacementRow>()
  for (const row of placementRows) {
    if (!publishedTeamIds.has(row.team_id)) continue
    const current = placed.get(row.employee_id)
    if (!current || row.effective_from > current.effective_from) placed.set(row.employee_id, row)
  }
  const people = Array.from(placed.values()).map((row) => ({
    employeeId: row.employee_id,
    identifier: row.employees?.employee_identifier ?? '',
    employee: row.employees?.display_name ?? publishedNames.get(row.employee_id) ?? UNKNOWN_OFFICER,
    teamId: row.team_id,
    title: row.title,
    shift: row.shift_templates?.name ?? '',
  }))
  return { people, entries, publishedTeamIds }
}
